/**
 * Tasks → Table view.
 *
 * UAT-48 introduced the two-section ledger (Open + Closed) with a single
 * top-right period dropdown. UAT-52 (Round 8 PR #52) narrows the filter's
 * remit: Open shows every open task at all times so overdue work cannot
 * vanish when Peter picks Today or This week; the period dropdown now
 * only scopes the Closed section. The earlier shape had open rows
 * filtered by `dueDate`, which hid overdue tasks the moment the user
 * narrowed the window. The dropdown still ranges over All / This year /
 * This month (default) / This week / Today.
 *
 * Behaviour summary:
 *   - The period dropdown holds the only filter state. Default is the
 *     current month. The dropdown scopes the Closed section only — Open
 *     is always full.
 *   - OPEN renders every open task in the list, full stop. No date
 *     window. Overdue and unscheduled tasks always render so nothing
 *     hides behind a period narrow.
 *   - CLOSED renders completed tasks whose `completedAt` falls inside
 *     the window. The list hook (`useCompletedTasksSince`) handles the
 *     fetch; the period's start date becomes the `since` parameter so
 *     the server-side `completedAt >= since` filter does the right thing.
 *   - Sections collapse via the heading chevron. Open is expanded by
 *     default; Closed is collapsed by default — the morning rhythm is
 *     "what is still on me" first, "what did I close" second.
 *
 * Reference: spec section 15.5, UAT-48, UAT-52.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useCompletedTasksSince, useTasks } from '@/lib/api/tasks';
import { startOfPeriod } from '@/lib/utils';
import { TaskTable } from '@/components/tasks/TaskTable';
import type { Task } from '@workspace/shared';

export const Route = createFileRoute('/tasks/table')({
  component: TasksTableView,
});

/** Period filter options surfaced by the top-right dropdown. */
export type Period = 'all' | 'year' | 'month' | 'week' | 'today';

/** Ordered list of options the period dropdown renders. The order
 * matches the UAT-48 spec: broadest window first, narrowest last. */
const PERIOD_OPTIONS: ReadonlyArray<{ value: Period; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'year', label: 'This year' },
  { value: 'month', label: 'This month' },
  { value: 'week', label: 'This week' },
  { value: 'today', label: 'Today' },
];

/** Shared dark-UI <select> classes for the period filter. Mirrors the
 * mono-label palette used elsewhere in the shell. */
const PERIOD_SELECT_CLASS =
  'border border-mx-grey-800 bg-mx-card text-mx-grey-300 px-mx-3 py-[6px] text-mx-mono';

/**
 * Tasks Table view. Exported so the component test can render the view
 * without spinning up a TanStack Router context. The Route definition
 * above is the only production consumer.
 */
export function TasksTableView() {
  const [period, setPeriod] = useState<Period>('month');
  const [collapsed, setCollapsed] = useState<{ open: boolean; closed: boolean }>({
    open: false,
    closed: true,
  });

  // Open list comes from the standard list hook. Round 8 PR #52: no
  // client-side filtering at all — Peter's "Closed All would be a huge
  // table" rationale only applies to the closed list; narrowing Open by
  // dueDate hid overdue tasks the moment the user picked Today or This
  // week, which is the opposite of what the Table tab is for.
  const open = useTasks();
  // Closed list uses the dedicated since-windowed hook. The since date
  // matches the period's start so the backend `completedAt >= since`
  // filter does the heavy lifting.
  const since = startOfPeriod(period);
  const completed = useCompletedTasksSince(since);

  // Open section always renders every open task regardless of the
  // period filter. See the file-top docstring for the UAT-52 rationale.
  const openRows: Task[] = open.data?.tasks ?? [];

  const closedRows: Task[] = completed.data?.tasks ?? [];

  if (open.isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (open.isError) return <p className="text-mx-body-sm text-mx-neg">Could not load tasks.</p>;

  return (
    <div className="flex flex-col gap-mx-6">
      {/* Top bar: the period filter sits flush-right so it never crowds
          the section eyebrows below. The small caption underneath
          reminds Peter that the dropdown only scopes the Closed
          section — Open is always full. Round 8 PR #52. */}
      <div className="flex flex-col items-end gap-mx-1">
        <label className="mono-label flex items-center gap-mx-2 text-mx-grey-mid">
          <span>Period</span>
          <select
            aria-label="Period filter"
            data-testid="period-filter"
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className={PERIOD_SELECT_CLASS}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-mx-card text-mx-grey-300">
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <p className="text-mx-mono-sm text-mx-grey-mid">Closed section only</p>
      </div>

      <CollapsibleSection
        eyebrowNum="A"
        label="Open"
        emptyCopy="No open tasks."
        rows={openRows}
        showStatusColumn
        collapsed={collapsed.open}
        onToggle={() => setCollapsed((c) => ({ ...c, open: !c.open }))}
        testId="table-section-open"
      />
      <CollapsibleSection
        eyebrowNum="B"
        label="Closed"
        emptyCopy="Nothing completed in this period."
        rows={closedRows}
        collapsed={collapsed.closed}
        onToggle={() => setCollapsed((c) => ({ ...c, closed: !c.closed }))}
        testId="table-section-closed"
      />
    </div>
  );
}

/**
 * Collapsible section wrapper.
 *
 * The header is a `<button>` so keyboard users get the same toggle
 * affordance as mouse users. The chevron flips between right (collapsed)
 * and down (expanded); the section body conditionally renders so a
 * collapsed section adds nothing to the DOM beyond its heading.
 */
function CollapsibleSection({
  eyebrowNum,
  label,
  rows,
  emptyCopy,
  showStatusColumn = false,
  collapsed,
  onToggle,
  testId,
}: {
  eyebrowNum: string;
  label: string;
  rows: Task[];
  emptyCopy: string;
  showStatusColumn?: boolean;
  collapsed: boolean;
  onToggle: () => void;
  testId: string;
}) {
  const Chevron = collapsed ? ChevronRightIcon : ChevronDownIcon;
  return (
    <section data-testid={testId}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={`${testId}-body`}
        className="mono-label mb-mx-2 flex w-full items-center gap-mx-2 text-mx-grey-mid hover:text-mx-grey-300"
      >
        <Chevron className="h-4 w-4" strokeWidth={1.5} aria-hidden />
        <span className="num">{eyebrowNum}</span>
        <span>{label}</span>
        <span className="tnum text-mx-grey-mid">({rows.length})</span>
      </button>
      {!collapsed && (
        <div id={`${testId}-body`}>
          {rows.length === 0 ? (
            <p className="text-mx-body-sm text-mx-grey-mid">{emptyCopy}</p>
          ) : (
            <TaskTable rows={rows} showStatusColumn={showStatusColumn} emptyCopy={emptyCopy} />
          )}
        </div>
      )}
    </section>
  );
}
